<template>
  <div class="reports">
    <h1>Relatórios dos Formulários Enviados no Site</h1>
    
    <div class="reports-controls">
      <button class="btn btn-primary export-btn" @click="exportData">
        <font-awesome-icon icon="download"/> Exportar Dados
      </button>
      <div class="filter-controls">
        <select v-model="selectedForm" @change="filterByForm" class="form-select">
          <option value="">Todos os formulários</option>
          <option v-for="form in formTypes" :key="form" :value="form">{{ form }}</option>
        </select>
        <input 
          type="date" 
          v-model="dateFrom" 
          @change="filterByDate"
          class="date-input"
          placeholder="Data inicial"
        >
        <input 
          type="date" 
          v-model="dateTo" 
          @change="filterByDate"
          class="date-input"
          placeholder="Data final"
        >
      </div>
    </div>

    <div class="reports-wrapper">
      <div v-if="loading" class="loader">
        <p>Carregando dados dos formulários...</p>
      </div>

      <div v-if="!loading && submissions.length === 0" class="no-data">
        <p>Nenhum formulário encontrado.</p>
      </div>

      <!-- Resumo Estatístico -->
      <div v-if="!loading && submissions.length > 0" class="statistics-section">
        <h2>Resumo Estatístico</h2>
        <div class="stats-grid">
          <div class="stat-card">
            <h3>Total de Submissões</h3>
            <span class="stat-number">{{ filteredSubmissions.length }}</span>
          </div>
          <div class="stat-card">
            <h3>Formulários Diferentes</h3>
            <span class="stat-number">{{ Object.keys(submissionsByForm).length }}</span>
          </div>
          <div class="stat-card">
            <h3>Emails Únicos</h3>
            <span class="stat-number">{{ uniqueEmails.length }}</span>
          </div>
          <div class="stat-card">
            <h3>Hoje</h3>
            <span class="stat-number">{{ todaySubmissions }}</span>
          </div>
        </div>
      </div>

      <!-- Contagem por Formulário -->
      <div v-if="!loading && submissions.length > 0" class="form-counts-section">
        <h2>Contagem por Formulário</h2>
        <div class="form-counts">
          <div 
            v-for="(count, formName) in submissionsByForm" 
            :key="formName"
            class="form-count-item"
            @click="toggleFormDetails(formName)"
          >
            <div class="form-count-header">
              <h3>{{ formName }}</h3>
              <div class="form-count-info">
                <span class="count-badge">{{ count.length }} submissões</span>
                <font-awesome-icon 
                  :icon="expandedForms[formName] ? 'chevron-up' : 'chevron-down'"
                  class="expand-icon"
                />
              </div>
            </div>
            
            <!-- Detalhes expandidos -->
            <div v-if="expandedForms[formName]" class="form-details">
              <div class="submissions-list">
                <div 
                  v-for="submission in count" 
                  :key="submission.id"
                  class="submission-item"
                >
                  <div class="submission-info">
                    <strong>{{ submission.email }}</strong>
                    <small>{{ formatDate(submission.createdAt) }}</small>
                  </div>
                  <button 
                    class="btn btn-sm btn-outline view-btn"
                    @click="viewSubmissionDetails(submission)"
                  >
                    <font-awesome-icon icon="eye"/> Ver Detalhes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Lista Geral de Submissões -->
      <div v-if="!loading && submissions.length > 0" class="all-submissions-section">
        <h2>Todas as Submissões 
          <small>({{ filteredSubmissions.length }} itens)</small>
        </h2>
        
        <div class="submissions-table">
          <div class="table-header">
            <div class="col-form">Formulário</div>
            <div class="col-email">Email</div>
            <div class="col-date">Data/Hora</div>
            <div class="col-actions">Ações</div>
          </div>
          
          <div 
            v-for="submission in paginatedSubmissions" 
            :key="submission.id"
            class="table-row"
          >
            <div class="col-form">
              <span class="">{{ submission.form }}</span>
            </div>
            <div class="col-email">{{ submission.email }}</div>
            <div class="col-date">
              {{ formatDate(submission.createdAt) }}
            </div>
            <div class="col-actions">
              <button 
                class="btn btn-sm btn-outline"
                @click="viewSubmissionDetails(submission)"
              >
                Detalhes
              </button>
            </div>
          </div>
        </div>

        <!-- Paginação -->
        <div class="pagination" v-if="totalPages > 1">
          <button 
            class="btn btn-sm"
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            Anterior
          </button>
          <span class="page-info">
            Página {{ currentPage }} de {{ totalPages }}
          </span>
          <button 
            class="btn btn-sm"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            Próxima
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes -->
    <div v-if="selectedSubmission" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Detalhes da Submissão</h3>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        <div class="modal-body">          
          <div class="detail-row">
            <strong>Formulário:</strong> {{ selectedSubmission.form }}
          </div>
          <div class="detail-row">
            <strong>Email:</strong> {{ selectedSubmission.email }}
          </div>
          <div class="detail-row">
            <strong>Data de Envio:</strong> 
            {{ formatDate(submission.createdAt) }}
          </div>
          <!-- Adicione outros campos conforme necessário -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const axios = require('axios').default;

export default {
  name: 'FormReports',
  data() {
    return {
      loading: true,
      submissions: [],
      filteredSubmissions: [],
      selectedForm: '',
      dateFrom: '',
      dateTo: '',
      expandedForms: {},
      selectedSubmission: null,
      currentPage: 1,
      itemsPerPage: 20
    }
  },
  computed: {
    submissionsByForm() {
      const grouped = {};
      this.filteredSubmissions.forEach(submission => {
        if (!grouped[submission.form]) {
          grouped[submission.form] = [];
        }
        grouped[submission.form].push(submission);
      });
      
      // Ordenar por quantidade (maior primeiro)
      const sorted = {};
      Object.keys(grouped)
        .sort((a, b) => grouped[b].length - grouped[a].length)
        .forEach(key => {
          sorted[key] = [...grouped[key]].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        });
      
      return sorted;
    },
    
    formTypes() {
      return [...new Set(this.submissions.map(s => s.form))].sort();
    },
    
    uniqueEmails() {
      return [...new Set(this.filteredSubmissions.map(s => s.email))];
    },
    
    todaySubmissions() {
      const today = new Date().toDateString();
      return this.filteredSubmissions.filter(s => 
        new Date(s.createdAt).toDateString() === today
      ).length;
    },
    
    paginatedSubmissions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return [...this.filteredSubmissions]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredSubmissions.length / this.itemsPerPage);
    }
  },
  
  methods: {
    filterByForm() {
      this.applyFilters();
    },
    
    filterByDate() {
      this.applyFilters();
    },
    
    applyFilters() {
      let filtered = [...this.submissions];
      
      // Filtro por formulário
      if (this.selectedForm) {
        filtered = filtered.filter(s => s.form === this.selectedForm);
      }
      
      // Filtro por data
      if (this.dateFrom) {
        filtered = filtered.filter(s => new Date(s.createdAt) >= new Date(this.dateFrom));
      }
      
      if (this.dateTo) {
        const endDate = new Date(this.dateTo);
        endDate.setHours(23, 59, 59, 999);
        filtered = filtered.filter(s => new Date(s.createdAt) <= endDate);
      }
      
      this.filteredSubmissions = filtered;
      this.currentPage = 1;
    },
    
    toggleFormDetails(formName) {
      this.$set(this.expandedForms, formName, !this.expandedForms[formName]);
    },
    
    viewSubmissionDetails(submission) {
      this.selectedSubmission = submission;
    },
    
    closeModal() {
      this.selectedSubmission = null;
    },
    
    exportData() {
      // Implementar exportação para CSV/Excel
      const csvContent = this.generateCSV();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `formularios_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    },
    
    generateCSV() {
      const headers = ['ID', 'Formulário', 'Email', 'Data de Envio'];
      const rows = this.filteredSubmissions.map(s => [
        s.id,
        s.form,
        s.email,
        new Date(s.createdAt).toLocaleString('pt-BR')
      ]);
      
      return [headers, ...rows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');
    },

    formatDate(dateString) {
    console.log(dateString);
    
    // Parse the date string manually to avoid timezone interpretation
    let date;
    
    if (dateString.includes('T')) {
        // ISO format: force it to be treated as local time by ensuring no 'Z' suffix
        const localDateString = dateString.replace(/Z$/, '');
        date = new Date(localDateString);
    } else {
        // Date-only format: already treated as local
        date = new Date(dateString);
    }
    
    console.log(date);
    return date.toLocaleString('pt-BR');
}

  },
  
  created() {
    const self = this;
    axios.get(this.$apiUrl + 'formsubmissions/list')
      .then(response => {
        self.submissions = response.data.items || [];
        self.filteredSubmissions = [...self.submissions];
        self.loading = false;
      })
      .catch(error => {
        this.$alert('Erro ao carregar dados dos formulários', 'Erro', 'error');
        console.error((error.response && error.response.data && error.response.data.error) || error.message);
        self.loading = false;
      });
  }
}
</script>

<style scoped lang="scss">
@import "../../assets/scss/global";
@import "../../assets/scss/buttons";

.reports {
  height: 100vh;
  overflow-y: scroll;
  padding: 20px;
  background-color: #f8f9fa;
  
  h1 {
    color: $primary;
    margin-bottom: 30px;
    text-align: center;
  }
  
  .reports-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding: 20px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    
    .export-btn {
      background-color: #28a745;
      border: none;
    }
    
    .filter-controls {
      display: flex;
      gap: 15px;
      
      .form-select, .date-input {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 5px;
        font-size: 14px;
      }
    }
  }
  
  .loader {
    text-align: center;
    padding: 50px;
    
    p {
      color: $primary;
      font-size: 18px;
    }
  }
  
  .no-data {
    text-align: center;
    padding: 50px;
    background: white;
    border-radius: 10px;
    
    p {
      color: #666;
      font-size: 18px;
    }
  }
  
  .statistics-section {
    margin-bottom: 30px;
    
    h2 {
      color: $primary;
      margin-bottom: 20px;
    }
    
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 20px;
      
      .stat-card {
        background: white;
        padding: 25px;
        border-radius: 10px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        text-align: center;
        
        h3 {
          margin: 0 0 10px 0;
          color: #666;
          font-size: 14px;
          text-transform: uppercase;
        }
        
        .stat-number {
          font-size: 32px;
          font-weight: bold;
          color: $primary;
        }
      }
    }
  }
  
  .form-counts-section {
    margin-bottom: 30px;
    
    h2 {
      color: $primary;
      margin-bottom: 20px;
    }
    
    .form-count-item {
      background: white;
      border-radius: 10px;
      margin-bottom: 15px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      overflow: hidden;
      
      .form-count-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        cursor: pointer;
        
        &:hover {
          background-color: #f8f9fa;
        }
        
        h3 {
          margin: 0;
          color: $primary;
        }
        
        .form-count-info {
          display: flex;
          align-items: center;
          gap: 15px;
          
          .count-badge {
            background-color: $primary;
            color: white;
            padding: 5px 12px;
            border-radius: 15px;
            font-size: 12px;
            font-weight: bold;
          }
          
          .expand-icon {
            color: #666;
          }
        }
      }
      
      .form-details {
        border-top: 1px solid #eee;
        padding: 20px;
        background-color: #fafafa;
        
        .submissions-list {
          max-height: 300px;
          overflow-y: auto;
          
          .submission-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            border-bottom: 1px solid #eee;
            
            &:last-child {
              border-bottom: none;
            }
            
            .submission-info {
              strong {
                display: block;
                color: $primary;
              }
              
              small {
                color: #666;
              }
            }
            
            .view-btn {
              font-size: 12px;
            }
          }
        }
      }
    }
  }
  
  .all-submissions-section {
    h2 {
      color: $primary;
      margin-bottom: 20px;
      
      small {
        color: #666;
        font-weight: normal;
      }
    }
    
    .submissions-table {
      background: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      
      .table-header {
        display: grid;
        grid-template-columns: 1fr 2fr 1.5fr 80px;
        gap: 15px;
        padding: 15px 20px;
        background-color: $primary;
        color: white;
        font-weight: bold;
        font-size: 14px;
      }
      
      .table-row {
        display: grid;
        grid-template-columns: 1fr 2fr 1.5fr 80px;
        gap: 15px;
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
        align-items: center;
        text-align: left;
        
        &:last-child {
          border-bottom: none;
        }
        
        &:hover {
          background-color: #f8f9fa;
        }
        
        .form-badge {
          background-color: #e9ecef;
          color: $primary;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
          white-space: nowrap;
        }
      }
    }
    
    .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 15px;
      margin-top: 20px;
      
      .page-info {
        color: #666;
        font-size: 14px;
      }
    }
  }
  
  // Modal
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    
    .modal-content {
      background: white;
      border-radius: 10px;
      width: 500px;
      max-width: 90vw;
      max-height: 90vh;
      overflow-y: auto;
      text-align: left;
      
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #eee;
        
        h3 {
          margin: 0;
          color: $primary;
        }
        
        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
        }
      }
      
      .modal-body {
        padding: 20px;
        
        .detail-row {
          margin-bottom: 15px;
          
          strong {
            color: $primary;
            margin-right: 10px;
          }
        }
      }
    }
  }
}

// Responsividade
@media (max-width: 768px) {
  .reports {
    .reports-controls {
      flex-direction: column;
      gap: 15px;
      
      .filter-controls {
        flex-direction: column;
        width: 100%;
      }
    }
    
    .stats-grid {
      grid-template-columns: 1fr 1fr;
    }
    
    .submissions-table {
      .table-header, .table-row {
        grid-template-columns: 1fr 1fr;
        
        .col-date, .col-actions {
          display: none;
        }
      }
    }
  }
}
</style>